/* eslint-disable @typescript-eslint/explicit-member-accessibility */
/* eslint-disable no-magic-numbers */
import {
  Box3,
  BoxGeometry,
  BufferGeometry,
  EdgesGeometry,
  Frustum,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshNormalMaterial,
  MeshPhongMaterial,
  Object3D,
  Plane,
  Sphere,
  Vector2,
  Vector3,
  Web3DMeshMaterial,
} from '@technology/web3d-plugin-threejs';
import { Api } from '@technology/web3d/dist/esnext/Api';
import { Checksum } from '@technology/web3d/dist/esnext/Helpers/Checksum';
import { Vector3Const } from '@technology/web3d/dist/esnext/Helpers/common-utils.js';
import { getLogger } from '@trimble/logging';
import { LRUCache } from 'lru-cache';
import { FeaturesServiceSettings } from './config/features-service-settings';
import {
  FeatureMesh,
  FeaturesBufferGeomTranslator,
} from './expansion/features-buffer-geom-translator';
import type { LayerItem } from './expansion/maps-api/types/layer.types';
import { getTileMinMax } from './mesh-utils.js';
import { ObjectPool } from './object-pool';
import { FeaturesServiceDecoder } from './processing/features-service-decoder';
import { PromiseQueue } from './processing/promise-queue';
import {
  EMPTY_TILE,
  FeaturesServiceMeshData,
} from './types/features-service-mesh-data';
import { latLonToWebMercator } from './utils/mercator-utils';
import { logFeaturesNodeDebug } from './expansion/monitoring-tools/features-node-debug-logs';

export declare type TileMesh = Mesh<
  BufferGeometry,
  MeshPhongMaterial | MeshNormalMaterial | Web3DMeshMaterial | MeshBasicMaterial
>;

type TileState =
  | 'start'
  | 'loading'
  | 'rendered'
  | 'refining'
  | 'refined'
  | 'ready'
  | 'failed'
  | 'unloading';

const FAILED_RETRY_DELAY_MS = 5_000;

export class FeaturesServiceNode {
  public state: TileState = 'start';
  public isVisibleInFrame = false;
  public lastTouchedFrame = -1;
  private failedAt: number | null = null;
  /*
   * The mesh associated with this node.
   */
  public meshes: FeatureMesh[] | undefined | null;
  /*
   * The children of this node.
   */
  public children: FeaturesServiceNode[] | undefined | null;
  /*
   * The bounding box of the tile within in the scene, which should be in the
   * project's coordinate system.
   */
  public readonly box: Box3;

  private readonly featureToGeomTranslator;

  /** Lazily built layer metadata keyed by layer ID, used for per-layer mesh styling. */
  private layersById: ReadonlyMap<string, LayerItem> | null = null;

  /*
   * The bounding box of the tile in the server's coordinate system.
   * For terrain mesh tiles this is either in WGS84 (lat/lng) Web Mercator coordinates.
   */
  private readonly mapBox: Box3;

  private static readonly checksumFactory = (): Checksum => new Checksum();

  private static isAbortError(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      (error as { name: string }).name === 'AbortError'
    );
  }

  // private isTestTile(): boolean {
  //   // const t1 =
  //   //   this.zoomLevel === 17 &&
  //   //   this.tileIndex.x === 256638 &&
  //   //   this.tileIndex.y === 33849;
  //   return true;
  // }
  private readonly logger = getLogger();

  // This flag controls whether an array of debug information strings
  // is collected during the tile's lifecycle. This is invaluable for
  // troubleshooting complex loading and rendering issues. But it is a
  // memory leak if left on in production, so it is off by default.
  debugLog: boolean = false;

  private debugBoxHelper: LineSegments | null = null;

  /**
   * Adds a wireframe box visualisation of this tile's bounding box to the
   * scene. Useful for debugging frustum culling and bounding box accuracy.
   * Call `removeDebugBox()` to clean up.
   */
  public showDebugBox(color: number = 0x00ff00): void {
    this.removeDebugBox();

    const size = this.box.getSize(new Vector3());
    const center = this.box.getCenter(new Vector3());

    const boxGeom = new BoxGeometry(size.x, size.y, size.z);
    const edges = new EdgesGeometry(boxGeom);
    const line = new LineSegments(
      edges,
      new LineBasicMaterial({ color, depthTest: false })
    );
    line.position.copy(center);
    line.renderOrder = 999;

    this.root.add(line);
    this.debugBoxHelper = line;
  }

  /**
   * Removes the debug bounding box visualisation from the scene.
   */
  public removeDebugBox(): void {
    if (this.debugBoxHelper) {
      this.root.remove(this.debugBoxHelper);
      this.debugBoxHelper.geometry.dispose();
      (this.debugBoxHelper.material as LineBasicMaterial).dispose();
      this.debugBoxHelper = null;
    }
  }

  constructor(
    private readonly api: Api,
    private readonly mapSettings: FeaturesServiceSettings,
    private readonly root: Object3D,
    private readonly zoomLevel: number,
    public tileIndex: Vector2,
    public parent: FeaturesServiceNode | null,
    private readonly meshDataCache: LRUCache<number, FeaturesServiceMeshData>,
    private readonly meshDecoder: FeaturesServiceDecoder,
    private readonly objectPool: ObjectPool,
    private readonly meshDataPromiseQueue: PromiseQueue<FeaturesServiceMeshData>
    // private readonly initialBoundingBox?: Box3
  ) {
    this.featureToGeomTranslator = new FeaturesBufferGeomTranslator(
      (coords: [number, number]) => {
        // GeoJSON coords are WGS84 [lon, lat] degrees; mapToScene expects Web Mercator meters
        const mercator = latLonToWebMercator(coords[1], coords[0]);
        const position = new Vector2();
        this.mapSettings.mapToScene(
          new Vector2(mercator.x, mercator.y),
          position
        );
        return { x: position.x, y: position.y };
      },
      this.mapSettings.options?.elevationSampler
    );

    this.mapBox = new Box3();
    this.box = new Box3();

    const { min, max } = this.getTileMinMax();

    this.mapMapBoxToScene(min, max);
    // this.showDebugBox();
  }

  public dispose(): void {
    this.removeDebugBox();
    this.removeMesh();
    this.children?.forEach((child) => {
      child.dispose();
    });
    this.children = [];
  }

  public get tileId(): string {
    return `${this.tileIndex.x}-${this.tileIndex.y}-${this.zoomLevel}`;
  }

  private getTileMinMax(): { min: Vector2; max: Vector2 } {
    return getTileMinMax(
      this.tileIndex,
      this.zoomLevel,
      this.mapSettings.mapProjection.bounds
    );
  }

  private createMeshes(
    featuresCollection: FeaturesServiceMeshData
  ): FeatureMesh[] | undefined {
    // EMPTY_TILE sentinel means the server confirmed no features for this tile.
    if (featuresCollection === EMPTY_TILE) return [];
    return this.featureToGeomTranslator.createFeatureMeshes(
      featuresCollection,
      this.getLayersById()
    );
  }

  private getLayersById(): ReadonlyMap<string, LayerItem> {
    if (!this.layersById) {
      this.layersById = new Map(
        (this.mapSettings.options?.layerItems ?? [])
          .filter((layer): layer is LayerItem & { id: string } =>
            Boolean(layer.id)
          )
          .map((layer) => [layer.id, layer])
      );
    }
    return this.layersById;
  }

  public removeMesh(): void {
    this.meshes?.forEach((featureMesh) => {
      featureMesh.mesh.removeFromParent();
      FeaturesBufferGeomTranslator.disposeMesh(featureMesh.mesh);
    });

    // remove all meshes from the scene and dispose of their geometries and materials to free GPU memory. Setting the array to null allows the garbage collector to reclaim CPU memory used by the mesh data structures.
    this.meshes = null;
  }

  private readonly plane = new Plane();
  private readonly centerXYZ = this.vector3();

  private readonly sphere: Sphere = new Sphere();

  private checkFrustumVisibility(frustum: Frustum): boolean {
    // Quick check of bounding sphere first for better performance
    const box = this.box;
    return (
      frustum.intersectsSphere(box.getBoundingSphere(this.sphere)) &&
      frustum.intersectsBox(box)
    );
  }

  private cancelRequests(): void {
    this.meshDataPromiseQueue.remove(
      PromiseQueue.genKey(
        'features-service-mesh',
        this.tileIndex.x,
        this.tileIndex.y,
        this.zoomLevel
      )
    );
  }

  /**
   * Aborts an in-flight tile fetch and returns the node to `start` so
   * `update()` can request data again (e.g. after tile loading was suspended).
   */
  public cancelLoading(): void {
    if (this.state !== 'loading') {
      return;
    }

    this.cancelRequests();
    this.transitionTo('start');
  }

  /**
   * Unloads the tile, releasing all its associated resources. This includes
   * cancelling pending network requests, freeing GPU and CPU memory, and pending network requests for this tile.
   * recursively unloading all child tiles and
   */
  public unload(): void {
    if (this.state === 'unloading' || this.state === 'start') {
      return;
    }
    // A failed tile that leaves view should reset so it can retry on re-entry.
    if (this.state === 'failed') {
      this.transitionTo('start');
      return;
    }

    // Transition the state to indicate
    // that this tile is now being cleaned up. This prevents the renderer
    // from trying to use it in the current or subsequent frames.
    this.transitionTo('unloading');

    // Cancel pending network requests
    this.nodeLog('unloading tile, cancelling pending requests');
    this.cancelRequests();

    //Reset to initial state: After all resources are freed, transition
    // the tile back to the START state. This makes the node a clean slate,
    // ready to be reused later if it comes back into view, which is more
    // efficient than destroying and recreating the node object itself.
    logFeaturesNodeDebug(
      this.logger,
      'debug',
      'Transitioning to start because tile unloaded',
      { tileId: this.tileId }
    );
    this.transitionTo('start');

    // The mesh data (vertex buffers, index buffers,
    // textures, etc.) stored in GPU memory must be explicitly released.
    // Failure to do so is a primary cause of memory leaks.
    if (this.meshes) {
      this.removeMesh();
    }

    // Release CPU-side data: Clear references to any large data structures
    // held in CPU memory, such as the decoded mesh data, to allow the
    // garbage collector to reclaim the memory.
    // - Nothing at present

    // Recursively unload children: A parent tile is the gateway to its
    // children. When a parent is unloaded, its entire subtree of descendants
    // must also be unloaded to ensure a complete cleanup.
    if (this.children) {
      for (const child of this.children) {
        child.unload();
      }
      this.children = [];
    }
  }

  public unloadChildren(): void {
    if (!this.children || this.children.length === 0) {
      return;
    }

    for (const child of this.children) {
      child.unload();
    }

    this.children = [];
  }

  /**
   * Transitions the tile to a new state and logs the change for debugging.
   * Centralizing state changes in this function makes it easy to trace the
   * lifecycle of a tile.
   *
   * @param {TileState} newState - The state to transition to.
   */
  public transitionTo(newState: TileState): void {
    if (this.state === newState) {
      return;
    }

    const previousState = this.state;
    this.nodeLog(`State transition ${previousState} -> ${newState}`);
    logFeaturesNodeDebug(
      this.logger,
      'debug',
      `[FeaturesServiceNode ${this.tileId}] State transition ${previousState} -> ${newState}`,
      { tileId: this.tileId, from: previousState, to: newState }
    );

    this.state = newState;
    this.failedAt = newState === 'failed' ? Date.now() : null;
  }

  public calculateTileScreenSize(): number {
    if (!this.api.camera) {
      throw new Error('Camera not set on API');
    }
    const distance = this.api.camera.quaternion.equals(this.root.quaternion)
      ? this.plane
          .setFromNormalAndCoplanarPoint(
            Vector3Const.up.clone().applyQuaternion(this.root.quaternion),
            this.box.min
          )
          .distanceToPoint(this.api.camera.position)
      : this.api.camera.position.distanceTo(this.box.getCenter(this.centerXYZ));
    const sizeScreen = this.api.camera.getApproxPixelSize(
      this.box.min.distanceTo(this.box.max),
      distance
    );

    return sizeScreen;
  }

  /**
   * Ensures that the four child nodes for this tile exist. This method
   * lazily creates the child nodes the first time they are needed for
   * refinement. If the children have already been created, the function
   * does nothing.
   */
  private ensureChildrenExist(): void {
    if (this.children && this.children.length > 0) {
      return;
    }

    this.children = [];

    // Calculate the coordinates for the four child tiles based on the
    // parent's coordinates. In a standard quadtree tiling scheme, a tile
    // at (level, x, y) has four children at (level + 1).
    const childX1 = this.tileIndex.x * 2;
    const childX2 = this.tileIndex.x * 2 + 1;
    const childY1 = this.tileIndex.y * 2;
    const childY2 = this.tileIndex.y * 2 + 1;

    // Create the four child nodes (Southwest, Southeast, Northwest, Northeast).
    // Each new node is initialized with its correct coordinates and a
    // reference back to this parent node.
    const southwest = new FeaturesServiceNode(
      this.api,
      this.mapSettings,
      this.root,
      this.zoomLevel + 1,
      new Vector2(childX1, childY1),
      this,
      this.meshDataCache,
      this.meshDecoder,
      this.objectPool,
      this.meshDataPromiseQueue
    );

    const southeast = new FeaturesServiceNode(
      this.api,
      this.mapSettings,
      this.root,
      this.zoomLevel + 1,
      new Vector2(childX2, childY1),
      this,
      this.meshDataCache,
      this.meshDecoder,
      this.objectPool,
      this.meshDataPromiseQueue
    );

    const northwest = new FeaturesServiceNode(
      this.api,
      this.mapSettings,
      this.root,
      this.zoomLevel + 1,
      new Vector2(childX1, childY2),
      this,
      this.meshDataCache,
      this.meshDecoder,
      this.objectPool,
      this.meshDataPromiseQueue
    );

    const northeast = new FeaturesServiceNode(
      this.api,
      this.mapSettings,
      this.root,
      this.zoomLevel + 1,
      new Vector2(childX2, childY2),
      this,
      this.meshDataCache,
      this.meshDecoder,
      this.objectPool,
      this.meshDataPromiseQueue
    );

    this.children.push(southwest, southeast, northwest, northeast);
  }

  public update(
    frameNumber: number,
    frustum: Frustum,
    firstTilesRendered: boolean
  ): void {
    this.lastTouchedFrame = frameNumber;
    let isVisible = false;
    if (!firstTilesRendered || this.parent === null) {
      isVisible = true;
    } else {
      isVisible = this.checkFrustumVisibility(frustum);
    }

    if (!isVisible) {
      this.isVisibleInFrame = false;
      this.unload();
      return;
    }

    this.isVisibleInFrame = true;
    const sizeScreen = this.calculateTileScreenSize();
    const zoomInThreshold = this.mapSettings.imageSize * devicePixelRatio * 4;

    // A failed tile that is permanently visible (e.g. a root tile) will never
    // leave view and therefore never call unload(). Allow it to retry after a
    // backoff so transient errors are recoverable without a page refresh.
    if (
      this.state === 'failed' &&
      this.failedAt !== null &&
      Date.now() - this.failedAt > FAILED_RETRY_DELAY_MS
    ) {
      this.transitionTo('start');
    }

    // Ensure data is loaded for visible tiles.
    if (this.state === 'start' && !this.meshes) {
      void this.requestData();
    } else if (this.state === 'start' && this.meshes) {
      this.transitionTo('ready');
    }

    const belowMinZoom = this.zoomLevel < this.mapSettings.minZoomLevel;
    const shouldRefine =
      (belowMinZoom || sizeScreen > zoomInThreshold) &&
      this.zoomLevel < this.mapSettings.maxZoomLevel;

    if (this.state === 'rendered' && shouldRefine) {
      this.transitionTo('refining');
    } else if (this.state === 'refining' && !shouldRefine) {
      // Camera moved away, cancel refinement.
      this.transitionTo('rendered');
      this.unloadChildren();
    } else if (this.state === 'refined' && !shouldRefine) {
      // The camera has zoomed out, and this parent tile is now sufficiently
      // detailed. We should render it instead of its children.
      if (!this.meshes) {
        logFeaturesNodeDebug(
          this.logger,
          'warn',
          'No mesh in refined -> ready transition',
          { tileId: this.tileId }
        );
        this.transitionTo('start');
      } else {
        this.transitionTo('ready');
        if (this.children) {
          for (const child of this.children) {
            logFeaturesNodeDebug(
              this.logger,
              'debug',
              'Transitioning to start because showing parent',
              { tileId: child.tileId, parentTileId: this.tileId }
            );
            child.transitionTo('start');
          }
        }
      }
    } else if (
      this.state === 'refined' &&
      shouldRefine === true &&
      this.children?.some((c) => c.isVisibleInFrame === true && !c.meshes)
    ) {
      this.transitionTo('refining');
    }

    // Determine if we need to process children for this frame
    const shouldProcessChildren =
      this.state === 'refining' || this.state === 'refined';

    if (shouldProcessChildren) {
      this.ensureChildrenExist();

      for (const child of this.children!) {
        child.update(frameNumber, frustum, firstTilesRendered);
      }
    }

    if (this.state === 'refining') {
      const allChildrenReady = this.children!.every(
        (child) =>
          !child.isVisibleInFrame ||
          child.state === 'ready' ||
          child.state === 'refined' ||
          child.state === 'rendered'
      );

      const anyChildVisible = this.children!.some(
        (child) => child.isVisibleInFrame
      );

      if (allChildrenReady && anyChildVisible) {
        this.transitionTo('refined');
      }
    }
  }

  public readonly log: string[] = [];

  async requestData(): Promise<void> {
    if (this.root.visible === false) {
      return;
    }

    this.transitionTo('loading');
    this.nodeLog('requesting data');
    try {
      const meshData = await this.fetchAndDecodeMesh();

      this.nodeLog('feature mesh data fetched');
      this.meshes = this.createMeshes(meshData);

      if (this.meshes) {
        this.nodeLog('mesh created successfully');
        this.transitionTo('ready');
        this.nodeLog('mesh created and tile ready');

        // void this.api.camera?.fitToView(this.box, 0);
      } else {
        // If createMesh returns undefined, the tile will never become 'ready'.
        this.nodeLog('createMesh returned undefined, failing tile load');
        throw new Error('Mesh creation failed'); // Force into the catch block moving the tile back to 'start'
      }
    } catch (e) {
      if (FeaturesServiceNode.isAbortError(e)) {
        logFeaturesNodeDebug(
          this.logger,
          'debug',
          `[FeaturesServiceNode ${this.tileId}] Request aborted as expected.`,
          { tileId: this.tileId }
        );
        // Aborted intentionally (tile left view) — reset to start so it can
        // be re-requested if it comes back into view.
        this.transitionTo('start');
      } else {
        logFeaturesNodeDebug(
          this.logger,
          'error',
          `[FeaturesServiceNode ${this.tileId}] Error during requestData:`,
          { tileId: this.tileId },
          e
        );
        // Transient error (network, timeout, parse) — transition to 'failed'
        // so update() won't immediately retry at frame rate. The tile will
        // only be re-requested after it leaves view and unload() resets it.
        this.transitionTo('failed');
      }
      this.nodeLog('error fetching data: ' + (e as Error).message);
    }
  }

  private async fetchAndDecodeMesh(): Promise<FeaturesServiceMeshData> {
    const cacheKey = this.getCacheKey();
    if (this.meshDataCache.has(cacheKey)) {
      // Return the cached value (may be an empty FeatureCollection for tiles with no features).
      // Caching empty tiles avoids re-fetching and prevents the request-storm this PR addresses.
      return this.meshDataCache.get(cacheKey) ?? EMPTY_TILE;
    } else {
      const abortController = new AbortController();
      return this.meshDataPromiseQueue.add({
        priority: () => this.tilePriority(),
        cancelable: true,
        key: PromiseQueue.genKey(
          'features-service-mesh',
          this.tileIndex.x,
          this.tileIndex.y,
          this.zoomLevel
        ),
        task: async () => {
          logFeaturesNodeDebug(
            this.logger,
            'debug',
            'Fetching and decoding mesh for tile',
            { tileId: this.tileId, zoomLevel: this.zoomLevel }
          );
          const featuresData = await this.meshDecoder.fetchAndDecode(
            {
              x: this.tileIndex.x,
              y: Math.pow(2, this.zoomLevel) - 1 - this.tileIndex.y,
              z: this.zoomLevel,
            },
            abortController
          );
          this.meshDataCache.set(this.getCacheKey(), featuresData);

          return featuresData;
        },
        abortController,
      });
    }
  }

  public getCacheKey(): number {
    return FeaturesServiceNode.checksumFactory()
      .add(this.tileIndex.x)
      .add(this.tileIndex.y)
      .add(this.zoomLevel)
      .get();
  }

  /* Tiles that are visible should have higher priority
   * with tiles closer to the camera getting even higher priority.
   * Lower number = higher priority
   */
  private readonly tempVector = new Vector3();
  public tilePriority(): number {
    let priority = 0;
    if (!this.isVisibleInFrame) {
      priority = 10000;
    }
    if (!this.api.camera) {
      return Number.MAX_VALUE;
    }
    const distance = this.api.camera.position.distanceTo(
      this.box.getCenter(this.tempVector)
    );

    priority += distance / 100;

    return priority;
  }

  /**
   * Maps the tile's bounding box to the scene's coordinate system.
   * This is necessary because the terrain mesh tiles are in WGS84 or Web Mercator coordinates,
   * while the scene may use a different coordinate system.
   *
   * @param tileMin - The minimum coordinates of the tile in the map projection.
   * @param tileMax - The maximum coordinates of the tile in the map projection.
   */
  private mapMapBoxToScene(tileMin: Vector2, tileMax: Vector2): void {
    this.mapBox.min.set(tileMin.x, tileMin.y, 0);
    this.mapBox.max.set(tileMax.x, tileMax.y, 0);
    const corners = [
      this.vector3().set(tileMin.x, tileMin.y, 0),
      this.vector3().set(tileMax.x, tileMin.y, 0),
      this.vector3().set(tileMin.x, tileMax.y, 0),
      this.vector3().set(tileMax.x, tileMax.y, 0),
    ];

    for (const corner of corners) {
      this.mapSettings.mapToScene(corner, corner);
      this.box.expandByPoint(corner);
    }

    // Give the bounding box a minimum Z-extent so frustum intersection
    // checks don't fail on a zero-thickness box.
    this.box.min.z = Math.min(this.box.min.z, 0);
    this.box.max.z = Math.max(this.box.max.z, 500);

    logFeaturesNodeDebug(this.logger, 'debug', 'box', {
      tileId: this.tileId,
      min: {
        x: this.box.min.x,
        y: this.box.min.y,
        z: this.box.min.z,
      },
      max: {
        x: this.box.max.x,
        y: this.box.max.y,
        z: this.box.max.z,
      },
    });
  }

  /**
   * Executes a callback function on the current node and all its descendants
   * that are currently considered "visible".
   *
   * In a Level of Detail (LOD) system like a quadtree, a node is considered
   * the "visible" one if its own mesh is set to visible, implying its children are not.
   * This function traverses the tree and finds these specific nodes.
   *
   * @param fn The function to execute for each visible node found.
   */
  public traverseVisible(fn: (node: FeaturesServiceNode) => void): void {
    // A tile is the visible leaf when it is rendering its own mesh.
    // 'rendered' and 'refining' both show the tile's own mesh (refining
    // acts as a placeholder while children load). Stop traversal here.
    if (
      this.meshes?.length &&
      (this.state === 'rendered' || this.state === 'refining')
    ) {
      fn(this);
      return;
    }

    // Otherwise the children may be the visible leaves — recurse down.
    if (this.children) {
      for (const child of this.children) {
        child.traverseVisible(fn);
      }
    }
  }

  /*
   * Executes a callback function on the current node and all its descendants.
   * @param fn The function to execute for each node in the tree.
   */
  public traverseAll(fn: (node: FeaturesServiceNode) => void): void {
    fn(this);
    if (this.children) {
      for (const child of this.children) {
        child.traverseAll(fn);
      }
    }
  }

  public countTilesInTree(): number {
    let count = 1; // Count this node
    if (this.children) {
      for (const child of this.children) {
        count += child.countTilesInTree(); // Recursively count children
      }
    }
    return count;
  }

  /*
   * If debug logging is enabled, appends a log entry to the tile's log array.
   * This is useful for tracing the tile's lifecycle and diagnosing issues.
   * @param log - The log entry to append.
   */
  private nodeLog(log: string): void {
    if (this.debugLog) {
      this.log.push(log);
    }
  }

  private vector3(): Vector3 {
    // Taking out for now as it can cause subtle and not so subtle bugs (discovered while using vector2)
    // The main thing to avoid is using a pooled vector asn a property of any persistent class,
    // as when the pool hands it out again, the value will change.
    // return this.objectPool.getVector3();
    return new Vector3();
  }
}
